// Project data — single source of truth for /projects listing and /projects/:slug pages.
// Edit project content here; both pages re-render from this array.

export const projects = [
  {
    slug: "25mw-solar-park-barmer",
    title: "25 MW Ground-Mount Solar Park — I&C Package",
    location: "Barmer, Rajasthan",
    capacity: "25 MW",
    year: "2025",
    status: "Completed",
    scope: [
      "Pile foundation drilling & grouting",
      "MMS erection & alignment",
      "Module installation with torque records",
      "DC stringing & cable dressing",
      "AC works & ICR terminations",
      "Pre-commissioning testing & documentation",
    ],
    services: ["Solar Plant I&C", "Earth Work & Piling", "EPC / Balance of Plant"],
    image: "/img/project-solar-park.png",
    summary: "Full installation package from piling to commissioning support in hard desert strata.",
    overview: [
      "A 25 MW ground-mount package executed in Barmer district's rocky terrain — the kind of strata where rented augers stall and schedules slip. Yaduraj deployed its own DTH piling rig, hydraulic auger and JCB fleet to keep the foundation front continuously ahead of structure erection.",
      "Our crews delivered MMS erection, module installation with torque records, DC stringing and ICR works, closing the scope with IR test reports and a complete handover dossier.",
    ],
    highlights: [
      { t: "Hard-strata piling", d: "with own DTH rig" },
      { t: "Zero rental dependence", d: "across civil works" },
      { t: "Documented handover", d: "with full test records" },
    ],
  },
  {
    slug: "10mw-solar-ic-jaisalmer",
    title: "10 MW Solar I&C Package",
    location: "Jaisalmer, Rajasthan",
    capacity: "10 MW",
    year: "2024",
    status: "Completed",
    scope: [
      "MMS erection",
      "Module mounting & clamping",
      "DC stringing & connectorisation",
      "LT panel & inverter-side terminations",
      "IR/megger testing & punch-point closure",
    ],
    services: ["Solar Plant I&C", "Manpower & Material Supply"],
    image: "/img/project-solar-ic.png",
    summary: "Structure erection, module mounting and complete DC/AC works for a 10 MW block.",
    overview: [
      "A focused installation scope in Jaisalmer's solar corridor: structure alignment, module mounting and stringing executed by Yaduraj's own crews housed in company site campers — no idle days lost to mobilisation or local accommodation gaps.",
    ],
    highlights: [
      { t: "Self-housed crews", d: "via own site campers" },
      { t: "Continuous installation front", d: "through peak summer" },
      { t: "Clean punch-list closure", d: "at handover" },
    ],
  },
  {
    slug: "bess-installation-rajasthan",
    title: "5 MW / 10 MWh BESS Installation Support",
    location: "Rajasthan",
    capacity: "5 MW / 10 MWh",
    year: "2026",
    status: "Completed",
    scope: [
      "Container & PCS placement support",
      "DC battery terminations with torque control",
      "Auxiliary power & control cabling",
      "Earthing & safety systems",
      "OEM-coordinated commissioning support",
    ],
    services: ["BESS I&C", "Electrical Contracting"],
    image: "/img/project-bess-installation.png",
    summary: "Container placement, DC terminations and commissioning support for a battery storage plant.",
    overview: [
      "Battery storage is unforgiving on workmanship — torque, ferruling and polarity discipline decide commissioning day. Yaduraj supported container and PCS placement, executed DC battery terminations with full ferruling and torque control, and worked alongside the OEM team through pre-charging checks to commissioning.",
    ],
    highlights: [
      { t: "Ferrule-printed, torque-logged", d: "DC work" },
      { t: "OEM-coordinated", d: "commissioning" },
      { t: "Electrical-contractor-led", d: "supervision" },
    ],
  },
  {
    slug: "33kv-switchyard-jodhpur",
    title: "33/11 kV Switchyard & Electrical Works",
    location: "Jodhpur, Rajasthan",
    capacity: "33/11 kV",
    year: "2025",
    status: "Completed",
    scope: [
      "Transformer erection & alignment",
      "On-site oil filtration & dehydration",
      "HT terminations & busbar works",
      "Earthing grid & testing",
      "Megger, CT/PT & protection testing support",
    ],
    services: ["Electrical Contracting", "EPC / Balance of Plant"],
    image: "/img/project-switchyard.png",
    summary: "Transformer erection, oil filtration, HT terminations and testing for a switchyard package.",
    overview: [
      "A licensed-contractor scope end to end: transformer erection and on-site oil filtration with Yaduraj's own filtration machine, HT cable terminations, earthing works and the full test sequence — megger, CT/PT and protection support — documented for charging clearance.",
    ],
    highlights: [
      { t: "Own oil-filtration machine", d: "on site" },
      { t: "Licensed electrical execution", d: "" },
      { t: "Charging-ready documentation", d: "" },
    ],
  },
  {
    slug: "svg-harmonic-mitigation",
    title: "SVG Installation & Harmonic Mitigation",
    location: "Industrial Client, Gujarat",
    capacity: "—",
    year: "2025",
    status: "Completed",
    scope: [
      "SVG panel installation",
      "Power & control cabling, terminations",
      "Harmonic measurement support & tuning assistance",
      "Testing & commissioning with OEM",
    ],
    services: ["Electrical Contracting", "Industrial & Infrastructure Support"],
    image: "/img/project-svg-harmonics.png",
    summary: "Static Var Generator installation and harmonic solutions for an industrial power system.",
    overview: [
      "Power-quality penalties were eating into the client's energy economics. Yaduraj installed Static Var Generator panels, executed control and power cabling, and supported harmonic measurement and tuning — a niche electrical capability few I&C contractors carry in-house.",
    ],
    highlights: [
      { t: "Niche power-quality capability", d: "" },
      { t: "In-house licensed electrical team", d: "" },
      { t: "Measured improvement", d: "in power factor & harmonics" },
    ],
  },
  {
    slug: "piling-campaign-bikaner",
    title: "Utility-Scale Piling Campaign",
    location: "Bikaner Solar Zone, Rajasthan",
    capacity: "Multi-block",
    year: "2026",
    status: "Ongoing",
    scope: [
      "DTH & auger pile drilling across parallel fronts",
      "Hard-strata drilling",
      "On-site pile grouting",
      "Survey coordination & QC records",
    ],
    services: ["Earth Work & Piling", "EPC / Balance of Plant"],
    image: "/img/project-piling-campaign.png",
    summary: "High-volume pile foundation campaign across multiple blocks with own DTH and auger fleet.",
    overview: [
      "An ongoing high-volume foundation campaign where daily pile counts decide the project's critical path. Yaduraj's owned DTH rig, hydraulic auger and JCB piling machines are running parallel fronts across blocks, with on-site grouting from our self-loading concrete mixer.",
    ],
    highlights: [
      { t: "Parallel piling fronts", d: "with owned rigs" },
      { t: "Hard-strata capability", d: "" },
      { t: "Daily production reporting", d: "to client" },
    ],
  },
];

export const getProjectBySlug = (slug) => projects.find((p) => p.slug === slug);
